/* eslint-disable @typescript-eslint/no-explicit-any */
import { post, read } from '@/utils/request';
import type { WalletRechargeQuota } from '@/pages/Wallet/interface';
import i18n from '@/i18n';

import type { OrderRecords } from './interface';
import type { WalletAPI } from './typings';

const getBalance = () => read('wallet/info');
const getList = (params: WalletAPI.SearchParams, signal?: any) =>
  read<WalletAPI.List>('wallet/operation_list', params, undefined, undefined, { signal });

const recharge = (params: WalletAPI.RechargeParams) =>
  post('wallet/bill_recharge', params, i18n.t('water_bill_recharge_failed'));

const searchAccount = (params: WalletAPI.ChargeBackSearchParams) =>
  post<WalletAPI.ChargeBackListInfo[]>('wallet/manual_recharge_search', params);
const chargeBack = (params: WalletAPI.ChargeBackParams) =>
  post('wallet/manual_recharge', params, i18n.t('blocked_recharge_failed'));

// 水单充值上传附件
const uploadFile = (formdata: any) =>
  post('wallet/upload_bill_files', undefined, i18n.t('upload_failed'), undefined, {
    body: formdata,
  });

const mock = ''; // 【打开】 mock

// 获取订单详情，皮卡充值下单
const getPayoneerRechargeDebitInfo = (params: WalletAPI.PayoneerRechargeDebitParams) =>
  post<WalletAPI.PayoneerRechargeDebitInfo>(
    'wallet/payoneer_recharge_debit',
    params,
    i18n.t('failed_to_obtain_order_details'),
    undefined,
    { isCamel: false, isLower: false },
  );

// 获取皮卡授权用户信息
const getPayoneerUserInfo = () =>
  read<WalletRechargeQuota.Response.PayoneerUserInfo>(
    `${mock}wallet/payoneer_user_info`,
    undefined,
    undefined,
    undefined,
    { isCamel: false },
  );

// 获取皮卡授权链接
const getPayoneerOauthUrl = () =>
  read(`${mock}wallet/payoneer_oauth_url`, undefined, undefined, undefined, { isCamel: false });

// 钱包皮卡充值下单确认
const payoneerConfirm = (data: { order_num: string }) => {
  return post<WalletRechargeQuota.Response.PayoneerConfirm>(
    'wallet/payoneer_recharge_commit',
    data,
    i18n.t('order_submission_failed'),
    undefined,
    { isCamel: false, isLower: false },
  );
};

// 获取订单详情，Airwallex充值下单
const getAirwallexRechargeDebitInfo = (params: WalletAPI.PayoneerRechargeDebitParams) =>
  post<WalletAPI.PayoneerRechargeDebitInfo>(
    `${mock}wallet/airwallex_recharge_debit`,
    params,
    i18n.t('failed_to_obtain_order_details'),
    undefined,
    { isCamel: false, isLower: false },
  );

// 钱包Airwallex充值下单确认
const airwallexConfirm = (data: { order_num: string }) => {
  return post<WalletRechargeQuota.Response.AirwallexConfirm>(
    `${mock}wallet/airwallex_recharge_commit`,
    data,
    i18n.t('order_submission_failed'),
    undefined,
    { isCamel: false, isLower: false },
  );
};

// 获取订单详情，人民币充值下单
const getCnyRechargeDebitInfo = (params: WalletAPI.CnyRechargeDebitParams) =>
  post<WalletAPI.CnyRechargeDebitInfo>(
    `${mock}wallet/cny_recharge_debit`,
    params,
    i18n.t('failed_to_obtain_order_details'),
    undefined,
    { isCamel: false, isLower: false },
  );

// 钱包人民币下单确认
const cnyConfirm = (params: WalletRechargeQuota.Request.CnyCommit) => {
  return post<WalletRechargeQuota.Response.CnyConfirm>(
    `${mock}wallet/cny_recharge_commit`,
    params,
    i18n.t('order_submission_failed'),
    undefined,
    { isCamel: false, isLower: false },
  );
};

// 获取订单详情，WorldFirst充值下单
const getWorldFirstRechargeDebitInfo = (params: WalletAPI.WorldFirstRechargeDebitParams) =>
  post<WalletAPI.WorldFirstRechargeDebitInfo>(
    `${mock}wallet/worldfirst_recharge_debit`,
    params,
    i18n.t('failed_to_obtain_order_details'),
    undefined,
    { isCamel: false, isLower: false },
  );

const getPingPongRechargeDebitInfo = (params: WalletAPI.PayoneerRechargeDebitParams) =>
  post<WalletAPI.PayoneerRechargeDebitInfo>(
    'wallet/pingpong_recharge_debit',
    params,
    i18n.t('failed_to_obtain_order_details'),
    undefined,
    { isCamel: false, isLower: false },
  );

const worldfirstConfirm = (data: { order_num: string; redirect_url?: string }) => {
  const defaultRedirectUrl = `${window.location.origin}/wallet/record/order?cb_from=WORLDFIRST`;
  return post<WalletRechargeQuota.Response.WorldFirstConfirm>(
    `${mock}wallet/worldfirst_recharge_commit`,
    { ...data, redirect_url: data.redirect_url || defaultRedirectUrl },
    i18n.t('order_submission_failed'),
    undefined,
    { isCamel: false, isLower: false },
  );
};

const pingpongConfirm = (data: { order_num: string; redirect_url?: string }) => {
  return post<WalletRechargeQuota.Response.PingPongConfirm>(
    'wallet/pingpong_recharge_commit',
    data,
    i18n.t('order_submission_failed'),
    undefined,
    { isCamel: false, isLower: false },
  );
};

// 第三方充值记录列表
const getRecordList = (params: OrderRecords.Request.OrderRecordsInfo, signal?: any) => {
  const recharge_way = params?.recharge_way;
  const order_status = params?.order_status;
  const recharge_status = params?.recharge_status;

  const param = {
    ...params,
    recharge_way: Array.isArray(recharge_way) ? recharge_way?.join(',') : recharge_way,
    order_status: Array.isArray(order_status) ? order_status?.join(',') : order_status,
    recharge_status: Array.isArray(recharge_status) ? recharge_status?.join(',') : recharge_status,
    start_date: params?.submit_time?.[0],
    end_date: params?.submit_time?.[1],
    current_page: Number(params?.currentPage),
    page_size: Number(params?.pageSize),
  };

  delete param?.currentPage;
  delete param?.pageSize;
  delete param?.submit_time;

  return post<OrderRecords.Response.OrderRecordsList>(
    `${mock}wallet/third_recharge_record`,
    param,
    undefined,
    undefined,
    { signal, isCamel: false, isLower: false },
  );
};

// 获取HsVa账号信息
const getWalletVaInfo = () =>
  read<{ success: boolean; info: string }>('va/va_info', undefined, undefined, undefined, {
    isCamel: false,
    isLower: false,
    getResponse: true,
  });

export {
  getBalance,
  getList,
  recharge,
  searchAccount,
  chargeBack,
  uploadFile,
  getPayoneerRechargeDebitInfo,
  getPayoneerUserInfo,
  getPayoneerOauthUrl,
  payoneerConfirm,
  getAirwallexRechargeDebitInfo,
  getPingPongRechargeDebitInfo,
  airwallexConfirm,
  getRecordList,
  getCnyRechargeDebitInfo,
  cnyConfirm,
  getWorldFirstRechargeDebitInfo,
  worldfirstConfirm,
  pingpongConfirm,
  getWalletVaInfo,
};
